export interface ApiResponse<T = any> {
	success: boolean;
	data: T;
	message: string;
	code?: string;
}

export class ApiError extends Error {
	code: string|undefined;

	constructor(public response: ApiResponse) {
		super(response.message);
		this.code = response.code;
		this.name = 'ApiError';
	}
}

export class ApiAuthError extends ApiError {
	constructor(public response: ApiResponse) {
		super(response);
		this.name = 'ApiAuthError';
	}
}

export const useUrl = (path: string): string => {
	const base = import.meta.env.VITE_API_BASE as string;
	return `${base}${path}`;
};

export const useGetParameters = (path: string, obj: Record<string, string>): string => {
	const params = new URLSearchParams(obj);
	return `${path}?${params.toString()}`;
};

export const useApi = async <T = any>(path: string, options: RequestInit = {}): Promise<T> => {
	const url = useUrl(path);
	const headers: Record<string, string> = {
		'Content-Type': 'application/json',
		'Accept': 'application/json',
		...(options.headers as Record<string, string>)
	};

	// Safety net: never send Bearer null/undefined
	const auth = headers['Authorization'];
	if (auth && (auth === 'Bearer null' || auth === 'Bearer undefined')) {
		delete headers['Authorization'];
	}

	try {
		const response = await fetch(url, {
			...options,
			headers
		});
		const result: ApiResponse<T> = await response.json();

		if (!response.ok) {
			if (response.status === 401) {
				// noinspection ExceptionCaughtLocallyJS
				throw new ApiAuthError(result);
			}

			// noinspection ExceptionCaughtLocallyJS
			throw new ApiError(result);
		}

		if (!result.success) {
			// noinspection ExceptionCaughtLocallyJS
			throw new ApiError(result);
		}

		return result.data;
	} catch (error) {
		if (error instanceof ApiError) {
			console.error('API error:', error.message);
		} else if (error instanceof ApiAuthError) {
			throw error;
		} else {
			console.error('Fetch error:', error);
		}
		throw error;
	}
};
